import axios from 'axios'

/**
 * Bir hatanın KAYNAĞINI sınıflandırır. Sunum katmanı rengi, ikonu ve varsayılan
 * metni buradan seçer; çağıranların HTTP kodu yorumlaması gerekmez.
 */
export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'auth'
  | 'permission'
  | 'validation'
  | 'conflict'
  | 'notfound'
  | 'ratelimit'
  | 'server'
  | 'unknown'

/**
 * i18n'e erişimi olmayan katmanlar için son çare metinleri. Uygulamanın birincil
 * dili TR; `toUserError()` çağıran yerler bunları `t.errors.*` ile değiştirir.
 */
const DEFAULT_TR_MESSAGE: Record<ApiErrorKind, string> = {
  network: 'Sunucuya ulaşılamıyor. İnternet bağlantınızı kontrol edip tekrar deneyin.',
  timeout: 'İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.',
  auth: 'Oturumunuzun süresi doldu. Lütfen tekrar giriş yapın.',
  permission: 'Bu işlem için yetkiniz yok.',
  validation: 'Girdiğiniz bilgilerde bir sorun var. Lütfen kontrol edip tekrar deneyin.',
  conflict: 'Bu kayıt zaten mevcut.',
  notfound: 'Aradığınız kayıt bulunamadı.',
  ratelimit: 'Çok fazla istek gönderildi. Lütfen biraz bekleyip tekrar deneyin.',
  server: 'Sunucuda bir hata oluştu. Lütfen daha sonra tekrar deneyin.',
  unknown: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.',
}

/**
 * Backend TR ve açıklayıcı mesajlar üretiyor ({success:false, message}).
 * Bu sınıf o mesajı, HTTP kodunu ve makine-okur kodu KAYBETMEDEN taşır.
 *
 * Önceki `handleApiError` düz bir `Error` döndürüyordu; `.response` yok olduğu için
 * servislerdeki `error.response?.data?.message` okuması hep undefined dönüyor ve
 * backend mesajı jenerik metinle eziliyordu.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number | null
  readonly code: string | null
  /** Backend'in kullanıcıya gösterilebilir mesajı. Yoksa null. */
  readonly serverMessage: string | null
  readonly details: unknown

  constructor(
    kind: ApiErrorKind,
    init?: {
      status?: number | null
      code?: string | null
      serverMessage?: string | null
      details?: unknown
    }
  ) {
    const serverMessage = init?.serverMessage ?? null
    // Error.message her zaman GÖSTERİLEBİLİR bir metin tutar: böylece henüz
    // toUserError'a geçmemiş, `error.message` okuyan kod da anlamlı metin görür.
    super(serverMessage ?? DEFAULT_TR_MESSAGE[kind])
    this.name = 'ApiError'
    this.kind = kind
    this.status = init?.status ?? null
    this.code = init?.code ?? null
    this.serverMessage = serverMessage
    this.details = init?.details
    Object.setPrototypeOf(this, ApiError.prototype)
  }
}

export const kindFromStatus = (status: number): ApiErrorKind => {
  if (status === 401) return 'auth'
  if (status === 403) return 'permission'
  if (status === 404) return 'notfound'
  if (status === 408) return 'timeout'
  if (status === 409) return 'conflict'
  if (status === 413) return 'validation'
  if (status === 429) return 'ratelimit'
  if (status >= 500) return 'server'
  if (status >= 400) return 'validation'
  return 'unknown'
}

/**
 * Yanıt gövdesinden kullanıcıya gösterilebilir metni çıkarır.
 * Backend şekilleri: {success:false, message} ve {error} (image-proxy).
 * HTML/XML gövdeler (Vercel 413 sayfası, S3 SignatureDoesNotMatch) REDDEDİLİR --
 * ham XML/HTML kullanıcıya dökülmemeli; onun yerine kind'a göre yerel metin kullanılır.
 */
const extractServerMessage = (data: unknown): string | null => {
  if (typeof data === 'string') {
    const text = data.trim()
    if (!text || text.startsWith('<')) return null
    return text
  }
  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>
    const message = body['message']
    if (typeof message === 'string' && message.trim()) return message.trim()
    const error = body['error']
    if (typeof error === 'string' && error.trim()) return error.trim()
  }
  return null
}

const extractServerCode = (data: unknown): string | null => {
  if (data && typeof data === 'object') {
    const code = (data as Record<string, unknown>)['code']
    if (typeof code === 'string' && code.trim()) return code.trim()
  }
  return null
}

/**
 * Servis katmanı için: hatayı kullanıcı metnine çevirirken çağıranın BAĞLAMSAL
 * yedek metnini korur.
 *
 * `toApiError(error).message` asla boş dönmez; bu yüzden servislerdeki
 * `... .message || 'Şirket eklenirken hata oluştu'` kalıbı ölü koddu ve
 * sınıflandırılamayan hatalarda kullanıcı bağlamı olmayan jenerik metni görüyordu.
 * Öncelik `utils/errorMessage.ts:toUserError` ile aynıdır: backend mesajı önce,
 * sonra türe özgü metin, en sonda çağıranın bağlamsal metni.
 */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const apiError = toApiError(error)
  if (apiError.serverMessage) return apiError.serverMessage
  // Bu iki türde türe-özgü metin bağlamsal metinden daha zayıftır.
  if (apiError.kind === 'validation' || apiError.kind === 'unknown') return fallback
  return apiError.message
}

/** Her türlü hatayı (axios, Error, bilinmeyen) tek bir ApiError'a normalize eder. */
export const toApiError = (error: unknown): ApiError => {
  if (error instanceof ApiError) return error

  if (axios.isAxiosError(error)) {
    const response = error.response
    if (response) {
      return new ApiError(kindFromStatus(response.status), {
        status: response.status,
        code: extractServerCode(response.data),
        serverMessage: extractServerMessage(response.data),
        details: response.data,
      })
    }
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new ApiError('timeout', { details: error })
    }
    return new ApiError('network', { details: error })
  }

  // Ham JS hatası (programlama hatası): mesajı kullanıcıya SIZDIRMA, yalnız details'te tut.
  return new ApiError('unknown', { details: error })
}
